import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Modal } from '~/components/ui/modal'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Button } from '~/components/ui/button'
import { Textarea } from '~/components/ui/textarea'
import { CustomSelect } from '~/components/ui/custom-select'
import { ErrorBanner } from '~/components/ui/error-banner'
import {
    getActiveBusinessContext,
    upsertActiveBusinessContext,
} from '~/api/business-context'
import { listCombinations } from '~/api/assessment'
import { useAuthStore } from '~/stores/auth-store'
import type { AssessmentCombination, AssessmentScale } from '~/api/types'

export interface ProfileModalProps {
    open: boolean
    onClose: () => void
}

const SCALE_OPTIONS = [
    { value: '일반', label: '일반' },
    { value: '소규모', label: '소규모' },
]

/**
 * 내 정보 / 프로필 모달.
 * 상단에 로그인 사용자(읽기 전용)를 보여주고, 사업장 기본값(업장명·규모·업종·요약)을
 * 편집한다. 여기서 저장한 기본값은 새 평가·상담에 자동 주입된다(businessContextId).
 */
export function ProfileModal({ open, onClose }: ProfileModalProps) {
    const user = useAuthStore((s) => s.user)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saved, setSaved] = useState(false)

    const [title, setTitle] = useState('')
    const [scale, setScale] = useState<AssessmentScale>('일반')
    const [industry, setIndustry] = useState('')
    const [summary, setSummary] = useState('')
    // 실존 (규모·업종) 조합 매트릭스. 불가능 조합(예: 소규모-기타 식품판매업) 선택을 막는다.
    const [combos, setCombos] = useState<AssessmentCombination[]>([])

    // 선택한 규모에서 실제로 존재하는 업종만 남긴다(카탈로그 조합 기준).
    const validIndustriesForScale = useMemo(() => {
        return new Set(
            combos.filter((c) => c.scale === scale).map((c) => c.industry)
        )
    }, [combos, scale])

    // 업종 검색·선택 옵션 — 현재 규모에서 유효한 업종만. 저장된 값이 목록에 없으면(과거
    // 자유입력·조합 매트릭스 로드 전) 맨 앞에 포함해 표시는 유지한다.
    const industryOptions = useMemo(() => {
        const labels = [...validIndustriesForScale].sort((a, b) =>
            a.localeCompare(b, 'ko')
        )
        const withCurrent =
            industry && !labels.includes(industry)
                ? [industry, ...labels]
                : labels
        return withCurrent.map((l) => ({ value: l, label: l }))
    }, [validIndustriesForScale, industry])

    const titleRef = useRef<HTMLInputElement>(null)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // 열릴 때마다 활성 사업장 기본값을 새로 불러온다(없으면 빈 폼).
    useEffect(() => {
        if (!open) return
        let cancelled = false
        setLoading(true)
        setError(null)
        setSaved(false)
        listCombinations()
            .then((rows) => {
                if (!cancelled) setCombos(rows)
            })
            .catch(() => {})
        getActiveBusinessContext()
            .then((ctx) => {
                if (cancelled) return
                setTitle(ctx?.title ?? '')
                setScale(ctx?.scale ?? '일반')
                setIndustry(ctx?.industry ?? '')
                setSummary(ctx?.summary ?? '')
            })
            .catch(() => {
                if (!cancelled) setError('기본 정보를 불러오지 못했어요.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open])

    // 로드가 끝나면 첫 입력(업장명)으로 포커스를 이동한다.
    useEffect(() => {
        if (open && !loading) titleRef.current?.focus()
    }, [open, loading])

    // 규모를 바꿔 현재 업종이 그 규모에서 불가능한 조합이 되면 업종 선택을 비운다
    // (예: 소규모로 바꿨는데 저장값이 소규모에 없는 '기타 식품판매업'이면 재선택 유도).
    // 조합 매트릭스에 아예 없는 과거 자유입력 업종은 제약하지 않는다(표시 유지).
    useEffect(() => {
        if (combos.length === 0 || !industry) return
        const known = combos.some((c) => c.industry === industry)
        if (known && !validIndustriesForScale.has(industry)) setIndustry('')
    }, [combos, scale, industry, validIndustriesForScale])

    // 언마운트/닫힘 시 예약된 자동 닫기 타이머 정리.
    useEffect(
        () => () => {
            if (closeTimer.current) clearTimeout(closeTimer.current)
        },
        []
    )

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        const trimmed = title.trim()
        if (!trimmed || saving) return
        setSaving(true)
        setError(null)
        try {
            await upsertActiveBusinessContext({
                title: trimmed,
                scale,
                industry: industry.trim() || undefined,
                summary: summary.trim() || undefined,
            })
            setSaved(true)
            closeTimer.current = setTimeout(onClose, 1200)
        } catch {
            setError('저장하지 못했어요. 잠시 후 다시 시도해 주세요.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal open={open} onClose={onClose} title='내 정보'>
            {/* 사용자(읽기 전용) */}
            <div className='border-border bg-muted/40 flex items-center gap-3 rounded-md border p-3'>
                <span
                    className='bg-primary text-primary-foreground grid size-9 shrink-0 place-items-center rounded-md text-sm font-bold'
                    aria-hidden='true'>
                    {(user?.name || user?.email || '사').slice(0, 1)}
                </span>
                <div className='min-w-0'>
                    <p className='text-foreground truncate text-sm font-semibold'>
                        {user?.name || '사용자'}
                    </p>
                    <p className='text-muted-foreground truncate text-xs'>
                        {user?.email ?? ''}
                    </p>
                </div>
            </div>

            {loading ? (
                <div className='text-muted-foreground flex items-center gap-2 py-8 text-sm'>
                    <Loader2 className='size-4 animate-spin' />
                    기본 정보를 불러오는 중…
                </div>
            ) : (
                <form onSubmit={handleSubmit} className='mt-4 space-y-4'>
                    <div className='space-y-1.5'>
                        <Label htmlFor='profile-title'>업장명</Label>
                        <Input
                            id='profile-title'
                            ref={titleRef}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder='예: 1호점, 본점, 도시락 공장'
                            required
                        />
                    </div>

                    <div className='space-y-1.5'>
                        <Label htmlFor='profile-scale'>기본 규모</Label>
                        <CustomSelect
                            id='profile-scale'
                            ariaLabel='기본 규모'
                            value={scale}
                            options={SCALE_OPTIONS}
                            onValueChange={(v) =>
                                setScale(v as AssessmentScale)
                            }
                        />
                    </div>

                    <div className='space-y-1.5'>
                        <Label htmlFor='profile-industry'>업종</Label>
                        <CustomSelect
                            id='profile-industry'
                            ariaLabel='업종'
                            value={industry}
                            options={industryOptions}
                            onValueChange={setIndustry}
                            searchable
                            searchPlaceholder='업종 검색…'
                            placeholder='업종을 검색·선택하세요'
                        />
                    </div>

                    <div className='space-y-1.5'>
                        <Label htmlFor='profile-summary'>
                            사업장 요약{' '}
                            <span className='text-muted-foreground font-normal'>
                                (선택)
                            </span>
                        </Label>
                        <Textarea
                            id='profile-summary'
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            placeholder='상담·평가에 참고할 사업장 특징을 적어 주세요.'
                            rows={3}
                        />
                    </div>

                    <p className='text-muted-foreground text-xs'>
                        여기서 정한 기본 업장·규모는 새 평가·상담에 자동으로
                        적용돼요.
                    </p>

                    {error ? <ErrorBanner>{error}</ErrorBanner> : null}
                    {saved ? (
                        <ErrorBanner tone='success'>
                            기본 정보를 저장했어요
                        </ErrorBanner>
                    ) : null}

                    <div className='flex justify-end gap-2'>
                        <Button
                            type='button'
                            variant='ghost'
                            onClick={onClose}
                            disabled={saving}>
                            취소
                        </Button>
                        <Button
                            type='submit'
                            loading={saving}
                            disabled={!title.trim()}>
                            {saving ? '저장 중…' : '저장'}
                        </Button>
                    </div>
                </form>
            )}
        </Modal>
    )
}
